import { BalloonEventModel } from '../models';
import { UserModel } from '../models/UserModel';

export default abstract class UserRepository {
  protected _user: UserModel | null = null;

  set user(user: UserModel | null) {
    this._user = user;
  }

  public async changePassword(
    currentPassword: string | null,
    newPassword: string
  ): Promise<void> {
    if (!this._user) {
      throw new Error('User not authenticated');
    }
    await this._changePassword(currentPassword, newPassword);
  }

  public async uploadAvatar(file: File): Promise<void> {
    if (!this._user) {
      throw new Error('User not authenticated');
    }
    await this._uploadAvatar(file);
  }

  public async deleteAvatar(): Promise<void> {
    if (!this._user) {
      throw new Error('User not authenticated');
    }
    await this._deleteAvatar();
  }

  get user() {
    return this._user;
  }

  public async login(payload: any): Promise<UserModel> {
    this._user = await this._login(payload);
    return this._user;
  }

  public async register(payload: any): Promise<void> {
    await this._register(payload);
    // Don't set user - registration requires email verification
  }

  public async logout() {
    await this._logout();
    this._user = null;
  }

  public async deleteUser() {
    await this._deleteUser();
    this._user = null;
  }

  public async loadUser() {
    if (!this._user) return;
    this._user = await this._loadUser();
    await this.loadBookmarks();
  }
  public async loadBookmarks() {
    if (!this._user) return;
    const bookmarks = await this._loadBookmarks();
    this._user.bookmarkedBalloonEvents = bookmarks;
  }
  public async toggleBookmark(balloonEvent: BalloonEventModel) {
    if (!this._user) return;

    if (this._user.isBookmarked(balloonEvent)) {
      this._user.unbookmarkClientSide(balloonEvent);
      await this._unbookmark(balloonEvent.id);
    } else {
      this._user.bookmarkClientSide(balloonEvent);
      await this._bookmark(balloonEvent.id);
    }
    await this.loadBookmarks();
  }

  protected abstract _deleteUser(): Promise<void>;
  protected abstract _login(payload: any): Promise<UserModel>;
  protected abstract _register(payload: any): Promise<void>;
  protected abstract _logout(): Promise<void>;
  protected abstract _loadUser(): Promise<UserModel>;
  protected abstract _bookmark(id: string): Promise<void>;
  protected abstract _unbookmark(id: string): Promise<void>;
  protected abstract _changePassword(
    currentPassword: string | null,
    newPassword: string
  ): Promise<void>;
  protected abstract _uploadAvatar(file: File): Promise<void>;
  protected abstract _deleteAvatar(): Promise<void>;
  protected abstract _loadBookmarks(): Promise<BalloonEventModel[]>;
}
